import { Injectable, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { TokenStorage } from './token-storage.service';

const STORAGE_KEY_CLIENT = 'mm-booking-guest-client-id';
const STORAGE_KEY_MARINA = 'mm-booking-guest-marina-id';

/**
 * Guest deep-link context: `?clientId=` (operator tenant), optional `?marinaId=` (pre-selected
 * marina for booking / QR), session storage, or persisted login for client.
 */
@Injectable({ providedIn: 'root' })
export class GuestClientContextService {
  private readonly router = inject(Router);
  private readonly tokenStorage = inject(TokenStorage);
  private readonly clientId = signal<string | null>(this.readStorageClient());
  private readonly marinaId = signal<string | null>(this.readStorageMarina());

  readonly activeClientId = computed(() => {
    return this.clientId() ?? this.readStorageClient() ?? this.tokenStorage.registeredClientId() ?? null;
  });

  readonly activeMarinaId = computed(() => {
    return this.marinaId() ?? this.readStorageMarina() ?? null;
  });

  /** Preserves `?clientId=` and `?marinaId=` when navigating (QR flows). */
  readonly linkQueryParams = computed(() => {
    const out: Record<string, string> = {};
    const c = this.activeClientId();
    const m = this.activeMarinaId();
    if (c) {
      out['clientId'] = c;
    }
    if (m) {
      out['marinaId'] = m;
    }
    return out;
  });

  constructor() {
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => this.syncFromUrl());
    this.syncFromUrl();
  }

  private readStorageClient(): string | null {
    try {
      return sessionStorage.getItem(STORAGE_KEY_CLIENT);
    } catch {
      return null;
    }
  }

  private readStorageMarina(): string | null {
    try {
      return sessionStorage.getItem(STORAGE_KEY_MARINA);
    } catch {
      return null;
    }
  }

  private syncFromUrl(): void {
    const tree = this.router.parseUrl(this.router.url);
    const rawClient = tree.queryParams['clientId'];
    if (typeof rawClient === 'string' && rawClient.trim()) {
      const id = rawClient.trim();
      try {
        sessionStorage.setItem(STORAGE_KEY_CLIENT, id);
      } catch {
        /* ignore */
      }
      this.clientId.set(id);
    }
    const rawMarina = tree.queryParams['marinaId'];
    if (typeof rawMarina === 'string' && rawMarina.trim()) {
      const id = rawMarina.trim();
      try {
        sessionStorage.setItem(STORAGE_KEY_MARINA, id);
      } catch {
        /* ignore */
      }
      this.marinaId.set(id);
    }
  }

  /** Client UUID: query param, QR session, or value stored at guest login. */
  resolveClientId(): string | null {
    this.syncFromUrl();
    return this.clientId() ?? this.readStorageClient() ?? this.tokenStorage.registeredClientId() ?? null;
  }

  /** Marina UUID from query / session (QR pre-selection for booking). */
  resolveMarinaId(): string | null {
    this.syncFromUrl();
    return this.marinaId() ?? this.readStorageMarina() ?? null;
  }
}
